'use client';

import { createMachine, assign } from 'xstate';
// import type { State } from 'xstate'; // Removed due to XState version compatibility
import type { OrderReadyToSubmit } from '@/components/order/order.types';

/**
 * Order workflow state machine context
 */
export interface OrderContext {
  orderData: OrderReadyToSubmit | null;
  orderId: string | null;
  error: string | null;
  validationErrors: Record<string, string>;
  retryCount: number;
  timestamp: number | null;
}

/**
 * Order workflow events
 */
export type OrderEvent =
  | { type: 'START_ORDER'; orderData: OrderReadyToSubmit }
  | { type: 'VALIDATE' }
  | { type: 'VALIDATION_SUCCESS' }
  | { type: 'VALIDATION_FAILED'; errors: Record<string, string> }
  | { type: 'SUBMIT' }
  | { type: 'SUBMIT_SUCCESS'; orderId: string; timestamp: number }
  | { type: 'SUBMIT_FAILED'; error: string }
  | { type: 'PROCESS_PAYMENT' }
  | { type: 'PAYMENT_SUCCESS' }
  | { type: 'PAYMENT_FAILED'; error: string }
  | { type: 'CONFIRM_ORDER' }
  | { type: 'CANCEL_ORDER'; reason?: string }
  | { type: 'RETRY' }
  | { type: 'RESET' };

/**
 * Order workflow state machine states
 */
export type OrderState =
  | 'idle'
  | 'validating'
  | 'invalid'
  | 'validated'
  | 'submitting'
  | 'submitFailed'
  | 'submitted'
  | 'processingPayment'
  | 'paymentFailed'
  | 'confirmed'
  | 'cancelled';

/**
 * Order workflow state machine
 * Manages the complete order submission process with proper error handling and retry logic
 */
export const orderStateMachine = createMachine(
  {
    /** @xstate-layout N4IgpgJg5mDOIC5QAoC2BDAxgCwJYDswBKAOlwgBswBiAFQEkB5AUQDkBRAYQH0BpAQQByAOQCSACQBRAchUAwgGUAogJkzJIgCJl1ktgEbL2wdHlrJU65cg1bdBsGQaKycyRswDEZAlYDOQ18MKQAaECR1EkNHMk5icikvLz8KZz5tZwQXNwlVFylFZ2c9Ai8fSnT9IODwxAjLDyNGqwIrJsb7BzIXFyNjDycXZqGxkAA3BgJqJGQqAAVqABt6TAB3MmXsYlhsVCwJ6gJJgmwAWzwAVwTBgFtqXdoD2mn5n3nl9Y3txCON1pqyxtAYjVhLQAIj0ZowrrRhPk7o84pRWnJ5tRQjD5Dw9sIgA */
    id: 'orderWorkflow',
    initial: 'idle',

    context: {
      orderData: null,
      orderId: null,
      error: null,
      validationErrors: {},
      retryCount: 0,
      timestamp: null,
    } as OrderContext,

    states: {
      idle: {
        on: {
          START_ORDER: {
            target: 'validating',
            actions: assign({
              orderData: ({ event }) => event.orderData,
              error: null,
              validationErrors: {},
              retryCount: 0,
            }),
          },
        },
      },

      validating: {
        entry: 'validateOrder',
        on: {
          VALIDATION_SUCCESS: {
            target: 'validated',
          },
          VALIDATION_FAILED: {
            target: 'invalid',
            actions: assign({
              validationErrors: ({ event }) => event.errors,
            }),
          },
        },
      },

      invalid: {
        on: {
          VALIDATE: 'validating',
          START_ORDER: {
            target: 'validating',
            actions: assign({
              orderData: ({ event }) => event.orderData,
              error: null,
              validationErrors: {},
            }),
          },
          RESET: 'idle',
        },
      },

      validated: {
        on: {
          SUBMIT: 'submitting',
          START_ORDER: {
            target: 'validating',
            actions: assign({
              orderData: ({ event }) => event.orderData,
              error: null,
              validationErrors: {},
            }),
          },
        },
      },

      submitting: {
        entry: 'submitOrder',
        on: {
          SUBMIT_SUCCESS: {
            target: 'submitted',
            actions: assign({
              orderId: ({ event }) => event.orderId,
              timestamp: ({ event }) => event.timestamp,
              error: null,
              retryCount: 0,
            }),
          },
          SUBMIT_FAILED: {
            target: 'submitFailed',
            actions: assign({
              error: ({ event }) => event.error,
              retryCount: ({ context }) => context.retryCount + 1,
            }),
          },
        },
      },

      submitFailed: {
        on: {
          RETRY: [
            {
              target: 'submitting',
              guard: 'canRetry',
              actions: assign({
                error: null,
              }),
            },
            {
              target: 'cancelled',
              actions: assign({
                error: 'Maximum retry attempts exceeded',
              }),
            },
          ],
          RESET: 'idle',
        },
      },

      submitted: {
        on: {
          PROCESS_PAYMENT: 'processingPayment',
          CONFIRM_ORDER: 'confirmed',
          CANCEL_ORDER: {
            target: 'cancelled',
            actions: 'cancelOrder',
          },
        },
      },

      processingPayment: {
        entry: 'processPayment',
        on: {
          PAYMENT_SUCCESS: 'confirmed',
          PAYMENT_FAILED: {
            target: 'paymentFailed',
            actions: assign({
              error: ({ event }) => event.error,
            }),
          },
        },
      },

      paymentFailed: {
        on: {
          RETRY: 'processingPayment',
          CANCEL_ORDER: {
            target: 'cancelled',
            actions: 'cancelOrder',
          },
          RESET: 'idle',
        },
      },

      confirmed: {
        entry: 'markOrderAsConfirmed',
        type: 'final',
      },

      cancelled: {
        entry: 'markOrderAsCancelled',
        on: {
          RESET: 'idle',
        },
      },
    },
  },
  {
    guards: {
      canRetry: ({ context }) => context.retryCount < 3,
    },

    actions: {
      validateOrder: ({ context }) => {
        // This will be implemented by the service using the machine
        console.log('Validating order:', context.orderData);
      },

      submitOrder: ({ context }) => {
        console.log('Submitting order:', context.orderData);
      },

      processPayment: ({ context }) => {
        console.log('Processing payment for order:', context.orderId);
      },

      cancelOrder: ({ context, event }) => {
        console.log(
          'Cancelling order:',
          context.orderId,
          'reason:',
          event.type === 'CANCEL_ORDER' ? event.reason : 'Unknown',
        );
      },

      markOrderAsConfirmed: ({ context }) => {
        console.log('Order confirmed:', context.orderId);
      },

      markOrderAsCancelled: ({ context }) => {
        console.log('Order cancelled:', context.orderId);
      },
    },
  },
);

/**
 * Order validation rules
 */
export const validateOrderData = (
  orderData: OrderReadyToSubmit,
): { isValid: boolean; errors: Record<string, string> } => {
  const errors: Record<string, string> = {};

  // Validate cart
  if (!orderData.cart || orderData.cart.length === 0) {
    errors.cart = 'Cart cannot be empty';
  } else {
    orderData.cart.forEach((item, index) => {
      if (!item.variant_id) {
        errors[`cart.${index}.variant_id`] = 'Variant ID is required';
      }
      if (!item.quantity || item.quantity <= 0) {
        errors[`cart.${index}.quantity`] = 'Valid quantity is required';
      }
    });
  }

  // Validate delivery data
  if (!orderData.deliveryData) {
    errors.deliveryData = 'Delivery information is required';
  } else {
    const { deliveryData } = orderData;

    if (!deliveryData.fullName?.trim()) {
      errors['deliveryData.fullName'] = 'Full name is required';
    }

    if (!deliveryData.email?.trim()) {
      errors['deliveryData.email'] = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(deliveryData.email)) {
      errors['deliveryData.email'] = 'Valid email is required';
    }

    if (!deliveryData.phoneNumber?.trim()) {
      errors['deliveryData.phoneNumber'] = 'Phone number is required';
    }

    if (!deliveryData.city?.trim()) {
      errors['deliveryData.city'] = 'City is required';
    }

    if (!deliveryData.state?.trim()) {
      errors['deliveryData.state'] = 'State is required';
    }

    if (!deliveryData.zipCode?.trim()) {
      errors['deliveryData.zipCode'] = 'ZIP code is required';
    }

    if (!deliveryData.deliveryDate || deliveryData.deliveryDate < Date.now()) {
      errors['deliveryData.deliveryDate'] = 'Valid future delivery date is required';
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

/**
 * Order machine state type
 */
type OrderMachineState = { context: OrderContext; matches: (state: string) => boolean };

/**
 * Order state selectors for easy access to specific state information
 */
export const orderSelectors = {
  isIdle: (state: OrderMachineState) => state.matches('idle'),
  isValidating: (state: OrderMachineState) => state.matches('validating'),
  isInvalid: (state: OrderMachineState) => state.matches('invalid'),
  isValidated: (state: OrderMachineState) => state.matches('validated'),
  isSubmitting: (state: OrderMachineState) => state.matches('submitting'),
  isSubmitFailed: (state: OrderMachineState) => state.matches('submitFailed'),
  isSubmitted: (state: OrderMachineState) => state.matches('submitted'),
  isProcessingPayment: (state: OrderMachineState) => state.matches('processingPayment'),
  isPaymentFailed: (state: OrderMachineState) => state.matches('paymentFailed'),
  isConfirmed: (state: OrderMachineState) => state.matches('confirmed'),
  isCancelled: (state: OrderMachineState) => state.matches('cancelled'),

  canSubmit: (state: OrderMachineState) => state.matches('validated'),
  canRetry: (state: OrderMachineState) => state.matches('submitFailed') || state.matches('paymentFailed'),
  canCancel: (state: OrderMachineState) =>
    !state.matches('idle') && !state.matches('confirmed') && !state.matches('cancelled'),

  hasError: (state: OrderMachineState) => Boolean(state.context.error),
  hasValidationErrors: (state: OrderMachineState) => Object.keys(state.context.validationErrors).length > 0,

  isLoading: (state: OrderMachineState) =>
    state.matches('submitting') || state.matches('processingPayment') || state.matches('validating'),
  isComplete: (state: OrderMachineState) => state.matches('confirmed') || state.matches('cancelled'),
};
